import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeftRight, faGrip } from '@fortawesome/free-solid-svg-icons';
import type { ProductDesignerConfig, PrintColorOption } from '@customedge/shared';
import { useProductSetup } from '../hooks/useProductSetup';
import { useDesignStore } from '../store/designStore';
import { PreviewView } from './canvas/PreviewView';
import { Model3DPreview } from './canvas/Model3DPreview';

// Read-only 2D/3D preview with no live editing canvas — reuses the exact same
// PreviewView (2D, grid/slider) and Model3DPreview (3D, orbit + zoom +/- +
// rotate) the real designer uses, so viewers see the design on the true
// product photo / 3D wrap with proper zoom, not a single flat image.
//
// The trick that lets us reuse those components with no live editing canvas
// mounted: both composite from `sideColorCountJsonCache` (via
// snapshotCanvasJson in lib/designDownload.ts) whenever a side has no live
// Fabric canvas — and `loadFromTemplate` populates exactly that cache. So this
// only needs to (1)
// boot the product config into the store like the designer does
// (useProductSetup: sides, productConfig incl. glbModelUrl, variants) and (2)
// load the saved design JSON into the cache. No <DesignerWorkspace> required.
//
// Shared by the customer approval page (ApprovalPreview.tsx) and the
// embeddable ?mode=widget route (DesignWidgetRoot.tsx) — extracted here so
// both stay in lockstep instead of drifting as two copies.
export function DesignPreviewCanvas({
  designId, config, jsonObjects, selectedVariantOptions, colorVariantId, printMethod,
  printPositions, printMethodByPosition, printColorByPosition, compact,
}: {
  // Set into the shared designStore below so PreviewView's embroidery/
  // embossing/laser-engraving/doming status polling (which keys off
  // store.savedDesignId, the same field the live designer's own Preview tab
  // uses) actually runs here too. Without this, all four async-render
  // preview modules silently never appeared on the approval page or the
  // embeddable widget — always falling back to the plain flat client render,
  // even for a design whose real render had already finished server-side
  // (found 2026-09-01, round 2 gap-hunt iter3).
  designId: string;
  config: ProductDesignerConfig;
  jsonObjects: Record<string, any>;
  // The customer's saved Object Variant picks — applied to the store so the
  // 3D wrap (and any 2D variant preview overlay) renders the variant they
  // chose, not the model's default appearance.
  selectedVariantOptions?: Record<string, string>;
  // The customer's saved vendor Product Color Variant + print method — a
  // DIFFERENT selection from selectedVariantOptions above. Without these,
  // useProductSetup has no way to know which color/sides were actually
  // designed and silently falls back to the product's first color variant
  // with every print method's sides shown (found 2026-07-17: this is why a
  // saved design's 2D/3D preview showed the wrong/base color and an
  // inflated side count).
  colorVariantId?: string | null;
  printMethod?: string | null;
  // The multiselect print-position feature's own per-position resolution —
  // without these, this same "show every side" bug reappears for a product
  // with print positions configured, since useProductSetup's position
  // filter only otherwise reads `?printPositions=`/etc. from the URL, which
  // this route never sets. Found 2026-08-06 per direct user report that the
  // Magento React storefront's embedded widget (which renders through this
  // component via DesignWidgetRoot) still showed every image even after the
  // in-designer Preview tab was fixed — same bug class as colorVariantId/
  // printMethod above, just the position dimension instead of color/method.
  printPositions?: string[];
  printMethodByPosition?: Record<string, string>;
  printColorByPosition?: Record<string, PrintColorOption>;
  // Opt-in, unattended-context mode for the PDP's small floating "keep your
  // saved design in sight" popover (search.promofit.nl) — forces the 2D
  // slider with no chrome (no grid/slider toggle, no arrows, no caption) and
  // auto-advances between sides every few seconds. Never set by the existing
  // widget-popup ("Bekijk ontwerp") or approval-page callers, so their look
  // is unchanged.
  compact?: boolean;
}) {
  const { t } = useTranslation();
  useProductSetup(config, { colorVariantId, printMethod, printPositions, printMethodByPosition, printColorByPosition });
  const { loadFromTemplate, productConfig, variants, setSelectedVariantOptions, setSavedDesignId } = useDesignStore();
  const [view, setView] = useState<'2d' | '3d'>('2d');
  // Grid/slider mode for the 2D tab specifically — owned here (not inside
  // PreviewView, see its hideModeToggle prop) so the icon toggle can render
  // merged into the same row as the 2D/3D tabs below, instead of a second row.
  const [previewMode, setPreviewMode] = useState<'grid' | 'slider'>('slider');

  // Load the saved design once the product's sides exist in the store, so the
  // cache keys (`{imageId}-{areaId}`) line up with the sideKeys useProductSetup
  // just created. Depending on `productConfig` (set inside useProductSetup)
  // guarantees this runs after that setup, not racing it.
  useEffect(() => {
    if (!productConfig) return;
    if (jsonObjects && Object.keys(jsonObjects).length) loadFromTemplate(jsonObjects);
    // Apply the saved Object Variant picks — without this, the 3D wrap renders
    // every Variant at the model's default appearance instead of what was
    // actually configured.
    if (selectedVariantOptions && Object.keys(selectedVariantOptions).length) {
      setSelectedVariantOptions(selectedVariantOptions);
    }
    // See this prop's own comment above — PreviewView's async-render preview
    // polling (embroidery/embossing/laser-engraving/doming) is inert without
    // store.savedDesignId set.
    if (designId) setSavedDesignId(designId);
  }, [productConfig, jsonObjects, loadFromTemplate, selectedVariantOptions, setSelectedVariantOptions, designId, setSavedDesignId]);

  const has3d = !!productConfig?.glbModelUrl;

  // Default to the 2D tab even when 3D is available — the viewer can switch
  // manually via the toggle below. `view`'s useState() initializer above
  // already covers this; no effect needed to force a tab on mount.

  // Read-only "what you picked" summary — one chip per Variant, showing ONLY
  // the chosen option (name + swatch), never the full option list.
  const chosenOptions = variants.flatMap(v => {
    const selection = selectedVariantOptions?.[v.id];
    if (!selection) return [];
    if (selection.startsWith('custom:')) {
      const hex = selection.slice(7);
      return [{ key: v.id, label: v.label, name: hex, swatch: hex }];
    }
    const option = v.options.find(o => o.id === selection);
    if (!option) return [];
    return [{
      key: v.id, label: v.label, name: option.name,
      swatch: option.type === 'texture' && option.textureUrl ? `url:${option.textureUrl}` : (option.colorHex ?? null),
    }];
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', border: compact ? 'none' : '1px solid #e2e8f0', borderRadius: compact ? 0 : 8, overflow: 'hidden' }}>
      {/* compact mode (PDP floating popover) never shows this row — always 2D,
          always the auto-sliding view built below. Single merged row: 2D/3D
          tabs (only when a GLB model exists) on the left/center, grid/slider
          icon toggle on the right — previously the grid/slider toggle was a
          second row rendered by PreviewView itself (see its hideModeToggle
          prop), doubling the vertical chrome for no reason once both rows
          exist in the same header context. The icon toggle only makes sense
          for the 2D tab (grid/slider has no 3D equivalent), so it's hidden
          whenever the 3D tab is the active view. */}
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ flex: 1 }} />
          {has3d && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
              {(['2d', '3d'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setView(mode)}
                  style={{
                    padding: '5px 16px', border: '1px solid #e2e8f0', borderRadius: 20,
                    background: view === mode ? 'var(--selection)' : '#fff',
                    color: view === mode ? '#fff' : '#374151',
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  {mode === '2d' ? t('approval.view2d') : t('approval.view3d')}
                </button>
              ))}
            </div>
          )}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
            {view === '2d' && (['grid', 'slider'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setPreviewMode(mode)}
                aria-label={mode === 'grid' ? t('designer.previewGrid') : t('designer.previewSlider')}
                title={mode === 'grid' ? t('designer.previewGrid') : t('designer.previewSlider')}
                style={{
                  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid #e2e8f0', borderRadius: 8, padding: 0,
                  background: previewMode === mode ? 'var(--selection)' : '#fff',
                  color: previewMode === mode ? '#fff' : '#374151',
                  cursor: 'pointer',
                }}
              >
                <FontAwesomeIcon icon={mode === 'grid' ? faGrip : faLeftRight} fontSize={14} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Keep both mounted (just hidden) once 3D exists so switching tabs
          doesn't re-bake the model / re-composite every side each time. */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <div style={{ position: 'absolute', inset: 0, display: view === '2d' || compact ? 'flex' : 'none' }}>
          {/* 'slider' not the designer's default 'grid' as the INITIAL mode only
              — this component only ever runs inside a short, embedded box (the
              approval page's preview column, or the admin's ?mode=widget
              iframe), where grid mode's CSS Grid has a real Chromium clipping
              quirk at narrow initial widths (see PreviewView's own comment on
              `initialViewMode`). Once mounted, previewMode (owned here, via
              hideModeToggle) is what actually drives grid vs slider — compact
              mode still ignores it entirely and always runs the auto-sliding
              view (autoAdvanceMs/hideChrome unchanged). */}
          <PreviewView
            initialViewMode="slider"
            autoAdvanceMs={compact ? 3000 : undefined}
            hideChrome={compact}
            hideModeToggle={!compact}
            viewMode={previewMode}
            onViewModeChange={setPreviewMode}
          />
        </div>
        {has3d && !compact && (
          <div style={{ position: 'absolute', inset: 0, display: view === '3d' ? 'flex' : 'none' }}>
            <Model3DPreview showVariantSelector={false} defaultAutoRotate />
          </div>
        )}
      </div>

      {!compact && chosenOptions.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
          padding: '8px 12px', background: '#fff', borderTop: '1px solid #e2e8f0',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {t('approval.selectedOptions')}
          </span>
          {chosenOptions.map(o => (
            <span key={o.key} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 10px',
              fontSize: 12, color: '#374151',
            }}>
              {o.swatch && (
                <span style={{
                  width: 14, height: 14, borderRadius: '50%', border: '1px solid #cbd5e1', flexShrink: 0,
                  background: o.swatch.startsWith('url:') ? `url(${o.swatch.slice(4)}) center/cover` : o.swatch,
                }} />
              )}
              <span style={{ fontWeight: 600 }}>{o.label}:</span> {o.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
